import { createI18n } from 'vue-i18n'
import en from './locales/en'
import ar from './locales/ar'

/**
 * Two languages, and the direction that comes with each.
 *
 * Arabic is not only a translation: the whole layout mirrors. Rather than let
 * every component think about that, the direction is set once on <html> and the
 * stylesheet is written in logical properties, so "start" means left in English
 * and right in Arabic without a single conditional in a template.
 */
export const LOCALES = ['en', 'ar'] as const
export type Locale = (typeof LOCALES)[number]

export const LOCALE_NAMES: Record<Locale, string> = {
  en: 'English',
  ar: 'العربية',
}

const DIRECTIONS: Record<Locale, 'ltr' | 'rtl'> = {
  en: 'ltr',
  ar: 'rtl',
}

const STORAGE_KEY = 'loyalty.locale'

/**
 * What to open in. A deliberate choice outlives the browser's; otherwise an
 * Arabic-speaking operator would have to switch on every machine they use.
 */
function initialLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored && LOCALES.includes(stored as Locale)) {
      return stored as Locale
    }
  } catch {
    // Private windows and locked-down browsers throw rather than return null.
  }
  const preferred = navigator.languages ?? [navigator.language]
  for (const tag of preferred) {
    const base = tag.split('-')[0]?.toLowerCase()
    if (LOCALES.includes(base as Locale)) {
      return base as Locale
    }
  }
  return 'en'
}

export const i18n = createI18n({
  // The Composition API mode: `legacy: false` is what makes useI18n() work.
  legacy: false,
  locale: initialLocale(),
  fallbackLocale: 'en',
  // A key with no Arabic falls back to English silently rather than warning in
  // the console on every render; the missing-key test is what catches those.
  missingWarn: false,
  fallbackWarn: false,
  messages: { en, ar },
})

export function currentLocale(): Locale {
  return i18n.global.locale.value as Locale
}

export function direction(locale: Locale = currentLocale()): 'ltr' | 'rtl' {
  return DIRECTIONS[locale]
}

/**
 * Switches language and mirrors the page.
 *
 * `lang` matters as much as `dir`: it is what tells the browser which font to
 * reach for, and what a screen reader switches pronunciation on.
 */
export function setLocale(locale: Locale): void {
  i18n.global.locale.value = locale
  document.documentElement.setAttribute('lang', locale)
  document.documentElement.setAttribute('dir', DIRECTIONS[locale])
  try {
    localStorage.setItem(STORAGE_KEY, locale)
  } catch {
    // Not being able to remember the choice is not a reason to refuse it.
  }
}

/** Applied before the app mounts, so the first paint is already the right way round. */
export function applyLocale(): void {
  setLocale(currentLocale())
}

/**
 * The tag to hand `Intl`. Arabic formatting is pinned to `ar-EG` with Latin
 * digits: the programme runs in Egypt, and a balance printed in Arabic-Indic
 * digits beside one the service rendered in Latin ones reads as two different
 * numbers.
 */
export function intlLocale(locale: Locale = currentLocale()): string {
  return locale === 'ar' ? 'ar-EG-u-nu-latn' : 'en-GB'
}
